import { useInvalidateReturnRequest } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useUpdateLineItem from "@babackapp/private-api-client/client/use-update-line-item";
import type { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { type PropsWithChildren, useCallback, useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import ArticleItemForm, {
	type ArticleItemFormValues,
} from "./article-item-form";

type EditArticleItemDialogProps = {
	lineItem: ReturnRequestLineItemDto;
	returnRequestId: string;
};

export default function EditArticleItemDialog({
	children,
	lineItem,
	returnRequestId,
}: PropsWithChildren<EditArticleItemDialogProps>) {
	const t = useTranslation();
	const [open, setOpen] = useState(false);
	const { data: shop } = useCurrentShopSuspense();
	const invalidateReturnRequest = useInvalidateReturnRequest();
	const { mutateAsync: updateLineItem } = useUpdateLineItem({
		onSuccess: () => {
			invalidateReturnRequest({
				returnRequestId: returnRequestId,
				shopId: shop.id,
			});
			setOpen(false);
		},
	});

	const onSubmit = useCallback(
		(data: ArticleItemFormValues) => {
			return updateLineItem({
				lineItemId: lineItem.line_item_id,
				harmonizedSystemCode: data.harmonizedSystemCode,
				countryOfOrigin: data.countryOfOrigin.code,
				returnRequestId,
				shopId: shop.id,
			});
		},
		[lineItem.line_item_id, returnRequestId, shop.id, updateLineItem],
	);

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<div>{children}</div>
			</DialogTrigger>
			<DialogContent className="pt-10">
				<DialogHeader>
					<DialogTitle>
						{t("returnDetail.update.updateArticleItem")}
					</DialogTitle>
				</DialogHeader>
				<ArticleItemForm
					onSubmit={onSubmit}
					defaultValues={{
						harmonizedSystemCode: lineItem.harmonizedSystemCode ?? "",
						countryOfOrigin: lineItem.countryOfOrigin
							? {
									name: lineItem.countryOfOrigin,
									code: lineItem.countryOfOrigin,
								}
							: {
									name: "",
									code: "",
								},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
}
